<template>
  <div>
    <div v-if="completedCount > 0" class="d-flex justify-content-end mb-2">
      <div class="form-check form-switch mb-0 text-nowrap">
        <input
          :id="showCompletedId"
          v-model="showCompleted"
          class="form-check-input"
          type="checkbox"
          role="switch"
        />
        <label class="form-check-label fs-10" :for="showCompletedId">
          {{
            $t("forwards.mandates.commitments.show_completed", {
              count: completedCount,
            })
          }}
        </label>
      </div>
    </div>
    <LoadingIndicatorComponent
      v-if="loading"
      :message="$t('forwards.mandates.commitments.loading')"
    />
    <DataTable
      v-else
      :value="shownRows"
      dataKey="row_id"
      class="border rounded"
      scrollable
      scrollHeight="400px"
      sortField="period_label"
      :sortOrder="1"
      resizableColumns
      columnResizeMode="fit"
      @column-resize-end="onColumnResizeEnd"
    >
      <template #empty>
        <div class="text-center py-4 text-muted">
          {{
            rows.length > 0
              ? $t("forwards.mandates.commitments.all_completed")
              : $t("forwards.mandates.commitments.empty")
          }}
        </div>
      </template>

      <Column
        field="committed_at"
        :header="$t('forwards.mandates.commitments.committed_on')"
        :style="columnStyle('committed_at')"
      >
        <template #body="{ data }">
          <span class="text-nowrap">{{
            formatDateTimeIntoIsoDate(data.committed_at)
          }}</span>
        </template>
      </Column>

      <Column
        field="period_label"
        :header="$t('forwards.mandates.commitments.period')"
        :style="columnStyle('period')"
        sortable
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
      </Column>

      <Column
        field="escrowed_mwh"
        :header="$t('forwards.mandates.commitments.escrowed')"
        :style="columnStyle('escrowed')"
        :headerStyle="{ minWidth: '120px' }"
      >
        <template #body="{ data }">
          <EscrowFillBarComponent
            :escrowed="data.escrowed_mwh"
            :total="data.volume_mwh"
          />
        </template>
      </Column>

      <Column
        field="window_end"
        :header="$t('forwards.mandates.commitments.window')"
        :style="columnStyle('window')"
      >
        <template #body="{ data }">{{ windowLabel(data) }}</template>
      </Column>

      <Column
        field="status"
        :header="$t('forwards.mandates.commitments.status')"
        :style="columnStyle('status')"
      >
        <template #body="{ data }">
          <ForwardsStatusBadgeComponent :status="data.status" />
        </template>
      </Column>

      <Column
        v-if="namesAnyDeal"
        :header="$t('forwards.mandates.commitments.deal')"
        :style="columnStyle('deal')"
      >
        <template #body="{ data }">
          <RouterLink
            v-if="data.deal_id !== null"
            :to="dealRoute(data.deal_id)"
          >
            {{
              data.deal_name ?? $t("forwards.mandates.commitments.open_deal")
            }}
          </RouterLink>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import {
  formatDate,
  formatDateTimeIntoIsoDate,
} from "@/composables/formatUtils";
import { usePersistedColumnWidths } from "@/composables/usePersistedColumnWidths";
import EscrowFillBarComponent from "@/forwards2/components/EscrowFillBarComponent.vue";
import ForwardsStatusBadgeComponent from "@/forwards2/components/ForwardsStatusBadgeComponent.vue";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import type { MandateCommitmentRow } from "@/forwards2/forwardsTypes";
import { getSortIcon } from "@/utils/sortIcons";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, ref, useId } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";

/**
 * The commitments standing on this mandate.
 *
 * Rows carry production period, escrow against the slice's volume, delivery window, status and when
 * the commitment was made — never a price, which belongs on the deal page. A pay-as-produced commitment
 * backs no slice, so it is one row over its contract: the window is its production period and delivery window both, and its escrow is measured
 * against the sum of the monthly nominations. The deal is named — and linked — exactly when the API filled
 * `deal_id`, which it does only for staff working as themselves, who alone open deal pages (user
 * decision, 2026-10-12); otherwise the cell stays empty, and the row never says who manages the deal,
 * because that would leak the same thing the name hides. The column is dropped when no row names one.
 */
const props = withDefaults(
  defineProps<{
    rows: MandateCommitmentRow[];
    loading?: boolean;
  }>(),
  { loading: false }
);

const { t } = useI18n();
const showCompletedId = useId();
const showCompleted = ref(false);
const namesAnyDeal = computed(() =>
  props.rows.some((row) => row.deal_id !== null)
);
const { columnStyle, onColumnResizeEnd } = usePersistedColumnWidths(
  "forwards2.mandate-commitments.v3",
  () => [
    "committed_at",
    "period",
    "escrowed",
    "window",
    "status",
    ...(namesAnyDeal.value ? ["deal"] : []),
  ]
);

const completedCount = computed(
  () => props.rows.filter((row) => row.status === "completed").length
);

const shownRows = computed(() =>
  showCompleted.value
    ? props.rows
    : props.rows.filter((row) => row.status !== "completed")
);

const windowLabel = (row: MandateCommitmentRow): string => {
  if (row.window_end === null) return t("forwards.common.no_window_end");
  if (row.window_start === null)
    return t("forwards.mandates.commitments.on_or_before", {
      date: formatDate(row.window_end),
    });
  return `${formatDate(row.window_start)} — ${formatDate(row.window_end)}`;
};
</script>
